import React, { useEffect, useState } from 'react';

import icons from '../../../assets/icons';
import AssetDeviceSelector from '../../../components/assetDeviceSelector';
import CustomButton from '../../../components/customButton';
import CustomInput from '../../../components/customInput';
import IconButton from '../../../components/iconButton';
import Tags from '../../../components/tags';
import utils from '../../../utils';

const Asset = ({ projectId, site, updateSite }) => {
  const [assets, setAssets] = useState(site.assets || (site.asset ? [site.asset] : []));
  const [displayName, setDisplayName] = useState('');
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [expandedIndex, setExpandedIndex] = useState(assets.length > 0 ? 0 : null);
  const [deviceSelectorKey, setDeviceSelectorKey] = useState(0);

  useEffect(() => {
    updateSite(previousSite => {
      const updatedSite = { ...previousSite, assets };
      delete updatedSite.asset;
      return updatedSite;
    });
  }, [assets]);

  const resetForm = () => {
    setDisplayName('');
    setSelectedDevices([]);
    setEditingIndex(null);
    setDeviceSelectorKey(previousKey => previousKey + 1);
  };

  const saveAsset = () => {
    const normalizedDisplayName = displayName.trim();
    if (!normalizedDisplayName) return;

    const nextAsset = {
      attachedDeviceIds: selectedDevices.map(device => device.id),
      attachedDevices: selectedDevices,
      displayName: normalizedDisplayName,
      type: 'UNKNOWN'
    };

    if (editingIndex === null) {
      setAssets([...assets, nextAsset]);
      setExpandedIndex(assets.length);
    } else {
      setAssets(assets.map((asset, index) => (index === editingIndex ? nextAsset : asset)));
      setExpandedIndex(editingIndex);
    }
    resetForm();
  };

  const editAsset = index => {
    const asset = assets[index];
    setDisplayName(asset.displayName);
    setSelectedDevices(
      asset.attachedDevices || (asset.attachedDeviceIds || []).map(deviceId => ({ id: deviceId, displayName: deviceId }))
    );
    setEditingIndex(index);
    setExpandedIndex(index);
    setDeviceSelectorKey(previousKey => previousKey + 1);
  };

  const removeAsset = indexToRemove => {
    setAssets(assets.filter((_asset, index) => index !== indexToRemove));
    if (expandedIndex === indexToRemove) {
      setExpandedIndex(null);
    } else if (expandedIndex > indexToRemove) {
      setExpandedIndex(expandedIndex - 1);
    }
    if (editingIndex === indexToRemove) {
      resetForm();
    } else if (editingIndex > indexToRemove) {
      setEditingIndex(editingIndex - 1);
    }
  };

  return (
    <div className="site-asset-create">
      <h4 className="section-label">Configure Assets and select their Devices.</h4>
      <div className="form-group">
        <label>Name</label>
        <CustomInput
          id="new-site-asset-name"
          type="text"
          value={displayName}
          isEditable
          autoFocus
          onChange={event => setDisplayName(event.target.value)}
        />
      </div>
      <div className="form-group site-device-section">
        <label>Devices (optional)</label>
        <AssetDeviceSelector
          key={deviceSelectorKey}
          projectId={projectId}
          siteId={site.id}
          site={site}
          onSiteUpdated={updateSite}
          selectedDevices={selectedDevices}
          onChange={setSelectedDevices}
          tagsClassName="site-asset-device-tags"
        />
      </div>
      <div className="site-asset-form-actions">
        {editingIndex !== null && (
          <CustomButton classes="btn-secondary" title="Cancel" handleClick={resetForm} />
        )}
        <CustomButton
          classes="btn-primary"
          title={editingIndex === null ? 'Add Asset' : 'Update Asset'}
          handleClick={saveAsset}
          disabled={!displayName.trim()}
        />
      </div>
      <div className="site-assets-draft-list">
        <div className="site-assets-draft-header">
          <h4>{`Assets (${assets.length})`}</h4>
        </div>
        {assets.length === 0 ? (
          <div className="site-assets-draft-empty">No Assets added.</div>
        ) : (
          assets.map((asset, index) => (
            <div className="site-asset-draft-row" key={`site-asset-${index}-${utils.toCamelCase(asset.displayName)}`}>
              <div className={`site-asset-draft ${expandedIndex === index ? 'active' : ''}`}>
                <button
                  type="button"
                  className="site-asset-draft-summary"
                  onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
                >
                  <div className="site-asset-draft-name">
                    <img src={icons.media} alt="asset" />
                    <h3 title={asset.displayName}>{asset.displayName}</h3>
                  </div>
                  <div className="site-asset-draft-count">
                    <img src={icons.deviceOutlineDark} alt="devices" />
                    <h3>{`${asset.attachedDeviceIds?.length || 0} Devices`}</h3>
                  </div>
                  <img className="site-asset-draft-arrow" src={icons.angleRight} alt="toggle Asset" />
                </button>
                {expandedIndex === index && asset.attachedDevices?.length > 0 && (
                  <div className="site-asset-draft-devices">
                    {asset.attachedDevices.map(device => (
                      <Tags key={`${index}-${device.id}`} value={device.displayName} />
                    ))}
                  </div>
                )}
              </div>
              <div className="site-asset-draft-actions">
                <IconButton
                  id={`edit-site-asset-${index}`}
                  iconClass="uil uil-edit-alt"
                  btnClasses="small"
                  tooltip="Edit Asset"
                  onPress={() => editAsset(index)}
                />
                <IconButton
                  id={`remove-site-asset-${index}`}
                  icon="del"
                  btnClasses="small"
                  tooltip="Remove Asset"
                  onPress={() => removeAsset(index)}
                />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Asset;
